import { Button as ButtonV1, Input } from "@red-digital/bricks";
import cn from "classnames";
import React, { useCallback, useEffect, useMemo, useRef, useState } from "react";
// Pulling ButtonV2 directly from the build folder due to the HOC not forwarding refs
import {
  TypeProduct_cardWithoutUnresolvableLinksResponse,
  TypeProgram_metaWithoutUnresolvableLinksResponse,
} from "@2u-landing-pages/types";
import { useIntersectionObserver } from "@2u-landing-pages/utils";
import EventClient from "@2u-landing-pages/utils/event-client";
import { Button } from "@red-digital/bricks/dist/components/Button/ButtonV2";
import { Icon } from "../../components/Icon";

import { formatDate } from "./filterAndSort";
import styles from "./FilterBar.module.scss";

type FilterBarProps = {
  programs?: TypeProduct_cardWithoutUnresolvableLinksResponse[];
  filters: any;
  setFilters: Function;
  numResults: number;
  filterGroups?: string[];
  filterLabels?: FilterLabels;
  showOptionTotals?: boolean;
  noRecommendedCard?: boolean;
};

type FilterOption = {
  name: string;
  value: string;
  total: number;
};

type FilterLabels = {
  [key: string]: string;
};

type FilterCfg = {
  [key: string]: FilterOption[];
};

const filterKeyMap = {
  School: "school_name",
  Topic: "topic",
  Duration: "duration",
  "Tuition Cost": "tuition_cost",
  "Weekly Commitment": "weekly_commitment",
  "Start Date": "start_date",
};

export const FilterBar: React.FC<FilterBarProps> = ({
  programs = [],
  filters,
  setFilters,
  numResults,
  filterGroups = ["School", "Topic", "Duration", "Weekly Commitment", "Start Date"],
  filterLabels = {},
  showOptionTotals = false,
  noRecommendedCard = false,
}) => {
  const [filterOpened, toggleFilterBar] = useState(false);
  const panelRef = useRef<HTMLDivElement>(null);
  const buttonRef = useRef<HTMLButtonElement>(null);
  const numResultsRef = useRef(numResults);

  useEffect(() => {
    numResultsRef.current = numResults;
  }, [numResults]);

  const handleIntersect = useCallback(() => {
    EventClient.elementViewed({
      webElement: {
        elementType: "Landing Page",
        htmlId: String(numResultsRef.current),
        location: "Gallery",
        position: "Gallery",
        text: String(numResultsRef.current),
      },
    });
  }, [numResultsRef]);

  const addObserver = useIntersectionObserver(handleIntersect, { threshold: 0.5 });
  const setObserver = useCallback(
    (element: HTMLElement | null) => {
      if (element) addObserver(element);
    },
    [addObserver]
  );

  // Close the filter bar when clicking outside of the panel and the button
  useEffect(() => {
    function handleClickOutside(event: MouseEvent) {
      if (
        panelRef.current &&
        !panelRef.current.contains(event.target as Node) &&
        buttonRef.current &&
        !buttonRef.current.contains(event.target as Node)
      ) {
        toggleFilterBar(false);
      }
    }

    document.addEventListener("mousedown", handleClickOutside);
    return () => {
      document.removeEventListener("mousedown", handleClickOutside);
    };
  }, []);

  const filterGroupOptions = useMemo(() => {
    const cfg: FilterCfg = {};

    filterGroups.forEach((group: string) => {
      const key = filterKeyMap[group as keyof typeof filterKeyMap];
      const collected: string[] = [];

      cfg[group] = [];

      programs?.forEach((program) => {
        let field = String(
          program?.fields?.program_meta?.fields[key as keyof TypeProgram_metaWithoutUnresolvableLinksResponse["fields"]]
        );

        if (field) {
          field = key !== "start_date" ? field : formatDate(field);
          if (!collected.includes(field)) {
            cfg[group].push({
              name: field,
              value: field,
              total: 1,
            });

            collected.push(field);
          } else {
            // We're not currently using the total count due to a last minute change in the design
            // but we can keep it here for future reference
            // and to avoid having to re-implement it later
            const index = cfg[group].findIndex((item: any) => item.value === field);

            if (index !== -1) {
              cfg[group][index].total += 1;
            }
          }
        }
      });
    });

    // Sort the options
    Object.keys(cfg).forEach((group) => {
      cfg[group].sort((a: FilterOption, b: FilterOption) => {
        if (group === "Start Date") {
          return new Date(a.value).getTime() - new Date(b.value).getTime();
        }

        if (group === "Duration") {
          const aSplit = a.value.split(" ");
          const bSplit = b.value.split(" ");

          if (aSplit.length === 2 && bSplit.length === 2) {
            if (aSplit[1].toLowerCase() === "weeks" && bSplit[1].toLowerCase() === "months") {
              return -1;
            } else if (aSplit[1].toLowerCase() === "months" && bSplit[1].toLowerCase() === "weeks") {
              return 1;
            }
          }

          const aDuration = parseInt(aSplit[0]);
          const bDuration = parseInt(bSplit[0]);
          return aDuration - bDuration;
        }

        return a.name.localeCompare(b.name);
      });
    });

    return cfg;
  }, [programs, filterGroups]);

  function onFilterChange(key: string, value: string, checked: boolean) {
    if (checked) {
      setFilters({
        ...filters,
        [key]: [...(filters[key] || []), value],
      });
    } else {
      const list = filters[key]?.filter((v: string) => v !== value);

      if (list.length === 0) {
        setFilters({
          ...filters,
          [key]: null,
        });
      } else {
        setFilters({
          ...filters,
          [key]: list,
        });
      }
    }
  }

  const handleResetFilters = () => {
    setFilters({});

    EventClient.elementClicked({
      webElement: {
        elementType: "Landing Page",
        htmlId: "",
        location: "Inline Filter",
        name: "Reset filters",
        position: "Inline Filter",
        text: "Reset filters",
      },
    });
  };

  return (
    <div
      className={cn(styles.galleryFilters, {
        [styles.noRecommendedCard]: noRecommendedCard,
      })}
    >
      <Button
        ref={buttonRef}
        className={styles.galleryFilterButton}
        variant="filled"
        color="light"
        icon={{
          component: <Icon name="filter" />,
          position: "left",
        }}
        onClick={() => {
          toggleFilterBar(!filterOpened);

          EventClient.elementClicked({
            webElement: {
              elementType: "Landing Page",
              htmlId: "",
              location: "Gallery",
              name: "Filters",
              position: "Gallery",
              text: "Filters",
            },
          });
        }}
      >
        Filters
      </Button>
      <div
        ref={panelRef}
        className={cn(styles.galleryFilterPanel, {
          [styles.galleryFilterPanelOpen]: filterOpened,
        })}
      >
        <div className={styles.galleryFilterHeader}>
          <h3>Filters</h3>
          <div>
            <ButtonV1 className={styles.galleryFiltersReset} onClick={handleResetFilters} size="medium">
              Reset filters
            </ButtonV1>
            <ButtonV1
              className={styles.galleryFilterClose}
              icon={{
                component: <Icon name="x" size="lg" />,
              }}
              onClick={() => {
                toggleFilterBar(false);

                EventClient.elementClicked({
                  webElement: {
                    elementType: "Landing Page",
                    htmlId: "",
                    location: "Inline Filter",
                    name: "Close button",
                    position: "Inline Filter",
                    text: "Close button",
                  },
                });
              }}
            />
          </div>
        </div>
        <div className={styles.galleryFilterGroupsContainer}>
          {Object.keys(filterGroupOptions).map((group) => (
            <div key={group} className={styles.galleryFilterGroup}>
              <span>{filterLabels?.[filterKeyMap[group as keyof typeof filterKeyMap]] || group}</span>
              {filterGroupOptions[group].map((option: any) => (
                <Input
                  key={option.value}
                  type="checkbox"
                  label={{
                    children: showOptionTotals ? `${option.name} (${option.total})` : option.name,
                  }}
                  checked={filters?.[filterKeyMap[group as keyof typeof filterKeyMap]]?.includes(option.value) || false}
                  onChange={(e) => {
                    onFilterChange(filterKeyMap[group as keyof typeof filterKeyMap], option.value, e.target.checked);

                    EventClient.elementClicked({
                      webElement: {
                        elementType: group,
                        htmlId: e.target.checked ? "add" : "remove",
                        location: "Inline Filter",
                        name: option.value,
                        position: group,
                        text: option.value,
                      },
                    });
                  }}
                />
              ))}
            </div>
          ))}
        </div>
      </div>
      <div className={styles.galleryFilterTotal} ref={setObserver}>
        <span>
          {numResults} Result{numResults === 1 ? "" : "s"}
        </span>
      </div>
    </div>
  );
};
